import { fetchJSON as fetchAPI } from './client';

export type BusinessTerm = {
  id: string;
  tenant_id: string;
  term: string;
  business_definition: string;
  technical_definition?: string;
  business_owner: string;
  technical_owner?: string;
  synonyms?: string[];
  status: string;
  lifecycle: string;
  registered_at: string;
};

export type CanonicalEntity = {
  id: string;
  tenant_id: string;
  name: string;
  display_name: string;
  description: string;
  kind: string;
  attributes?: string[];
  product_family?: string;
  reused_by?: string[];
  registered_at: string;
};

export type KPIDefinition = {
  id: string;
  tenant_id: string;
  identifier: string;
  business_name: string;
  formula: string;
  description: string;
  owner: string;
  refresh_policy: string;
  units: string;
  related_dimensions?: string[];
  related_products?: string[];
  security_classification: string;
  registered_at: string;
};

export type DimensionDefinition = {
  id: string;
  name: string;
  display_name: string;
  description: string;
  hierarchy?: string[];
  used_by?: string[];
};

export type KnowledgeGraphNode = {
  id: string;
  type: string;
  label: string;
  ref_id?: string;
};

export type KnowledgeGraphEdge = {
  id: string;
  from_id: string;
  to_id: string;
  relation: string;
  weight?: number;
};

export type KnowledgeGraph = {
  nodes: KnowledgeGraphNode[];
  edges: KnowledgeGraphEdge[];
};

export type LineageRecord = {
  id: string;
  tenant_id: string;
  metric_or_kpi: string;
  source_system: string;
  transformation?: string;
  aggregation?: string;
  calculation?: string;
  consumers?: string[];
  dashboards?: string[];
  apis?: string[];
  ai_agents?: string[];
};

export type MetadataEntry = {
  id: string;
  tenant_id: string;
  kind: string;
  name: string;
  source: string;
  description?: string;
  version?: string;
  completeness_pct: number;
  updated_at: string;
};

export type BusinessContext = {
  tenant_id: string;
  entity_summaries?: { name: string; description: string; relations?: string[] }[];
  kpi_context?: { identifier: string; name: string; formula: string; current_target?: number }[];
  policy_context?: { name: string; description: string }[];
  planning_context?: { plan_type: string; horizon: string; description: string }[];
  recommendation_context?: string[];
  decision_context?: string[];
  generated_at: string;
};

export type SemanticHealth = {
  tenant_id: string;
  semantic_health_score: number;
  glossary_coverage_pct: number;
  entity_reuse_rate_pct: number;
  duplicate_terms_detected: number;
  metadata_completeness_pct: number;
  knowledge_graph_health_pct: number;
  lineage_completeness_pct: number;
  semantic_adoption_pct: number;
  context_usage_24h: number;
  registered_entities: number;
  registered_kpis: number;
  glossary_terms: number;
  validated_at: string;
};

export type SemanticDashboard = {
  tenant_id: string;
  health: SemanticHealth;
  entities: number;
  glossary_terms: number;
  kpis: number;
  dimensions: number;
  metadata_entries: number;
  lineage_records: number;
  graph_nodes: number;
  graph_edges: number;
  extension_points: { dev_id: string; name: string; description: string }[];
  generated_at: string;
};

const SEMANTIC_BASE = import.meta.env.VITE_SEMANTIC_API ?? '/api/v1/semantic';

function fetchSemantic<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${SEMANTIC_BASE}${path}`, init);
}

export const semanticGovernanceApi = {
  listEntities: () =>
    fetchSemantic<{ entities: CanonicalEntity[] }>('/entities').then((r) => r.entities),

  listGlossary: () =>
    fetchSemantic<{ glossary: BusinessTerm[] }>('/glossary').then((r) => r.glossary),

  listKPIs: () =>
    fetchSemantic<{ kpis: KPIDefinition[] }>('/kpis').then((r) => r.kpis),

  listDimensions: () =>
    fetchSemantic<{ dimensions: DimensionDefinition[] }>('/dimensions').then((r) => r.dimensions),

  knowledgeGraph: () => fetchSemantic<KnowledgeGraph>('/knowledge-graph'),

  listLineage: () =>
    fetchSemantic<{ lineage: LineageRecord[] }>('/lineage').then((r) => r.lineage),

  listMetadata: () =>
    fetchSemantic<{ metadata: MetadataEntry[] }>('/metadata').then((r) => r.metadata),

  businessContext: (params?: { entity?: string; kpis?: string; include_policy?: boolean; include_planning?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.entity) q.set('entity', params.entity);
    if (params?.kpis) q.set('kpis', params.kpis);
    if (params?.include_policy) q.set('include_policy', 'true');
    if (params?.include_planning) q.set('include_planning', 'true');
    const qs = q.toString();
    return fetchSemantic<BusinessContext>(`/context${qs ? `?${qs}` : ''}`);
  },

  health: () => fetchSemantic<SemanticHealth>('/health'),

  dashboard: () => fetchSemantic<SemanticDashboard>('/dashboard'),
};
